import { Navigate, useLocation } from 'react-router-dom';
import { useAuthSessionState } from './AuthSessionState';
import { useEffect, useState, useRef } from 'react';
import { TermsProvider } from '@/contexts/TermsContext';
import { MatterSearchProvider } from '@/contexts/MatterSearchContext';
import { SpotlightSearch } from '@/components/SpotlightSearch/SpotlightSearch';
import Header from '@/components/SiteHeader/SiteHeader';
import SidebarMenu from '@/components/SidebarMenu/SidebarMenu';
import AuthenticatedAppRoutes from './AuthenticatedAppRoutes';
import { AppShell } from '@mantine/core';
import { useHotkeys } from '@mantine/hooks';
import { ScoutChat } from '@/components/ScoutChat';
import { ScoutChatProvider } from '@/components/ScoutChat/ScoutChatContext';
import { EmailAuthStatusProvider } from '@/hooks/EmailAuthStatusContext';
import { AutoEnableAragDemoForMarkEmulator } from '@/components/DevPanel/AutoEnableAragDemoForMarkEmulator';
import { isUsingEmulators } from '@/firebaseConfig';
import { useSidebarMenuItems } from '@/hooks/useSidebarMenuItems';

type ShadowEdge = 'top' | 'right' | 'bottom' | 'left';

const AuthenticatedAppLayout = () => {
  const authState = useAuthSessionState();
  const menuItems = useSidebarMenuItems();
  const location = useLocation();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [
    showFullScreenContinuationNotice,
    setShowFullScreenContinuationNotice,
  ] = useState(false);
  const [chatInitialText, setChatInitialText] = useState<string | undefined>(
    undefined,
  );
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const sidebarMenuRef = useRef<{ openMobileMenu: () => void }>(null);
  const wasOnChatPageRef = useRef(false);
  const MARK_DEMO_ACCOUNT_EMAIL = 'mark@lydagroup.com';

  // Toggle chat with Cmd/Ctrl + Shift + K
  useHotkeys([
    [
      'mod+shift+K',
      () => {
        setShowFullScreenContinuationNotice(false);
        setIsChatOpen((prev) => !prev);
      },
    ],
  ]);

  // When navigating away from /chat, open the drawer to continue the conversation
  useEffect(() => {
    const isOnChatPage = location.pathname === '/chat';

    if (isOnChatPage) {
      wasOnChatPageRef.current = true;
      // Close drawer when on chat page (full-page mode)
      setIsChatOpen(false);
      setShowFullScreenContinuationNotice(false);
    } else if (wasOnChatPageRef.current) {
      // User navigated away from /chat - open drawer to continue conversation
      wasOnChatPageRef.current = false;
      setShowFullScreenContinuationNotice(true);
      setIsChatOpen(true);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (authState.status !== 'authenticated' || !isUsingEmulators) {
      return;
    }
    if (
      authState.authSession.userEmail.toLowerCase() !== MARK_DEMO_ACCOUNT_EMAIL
    ) {
      return;
    }
    localStorage.setItem('practist:showDevPanel', 'false');
  }, [authState.status]);

  switch (authState.status) {
    case 'unauthenticated':
      // Redirect them to the /login page, but save the current location they were
      // trying to go to when they were redirected. This allows us to send them
      // along to that page after they login, which is a nicer user experience
      // than dropping them off on the home page.
      return <Navigate to='/login' state={{ from: location }} replace />;
    case 'unknown':
      return null;
    case 'authenticated':
      return (
        <TermsProvider authSession={authState.authSession}>
          <AutoEnableAragDemoForMarkEmulator />
          <EmailAuthStatusProvider>
            <MatterSearchProvider>
              <ScoutChatProvider>
                <SpotlightSearch
                  onOpenChat={(initialText) => {
                    setChatInitialText(initialText);
                    setShowFullScreenContinuationNotice(false);
                    setIsChatOpen(true);
                  }}
                >
                  <AppShell
                    aside={{
                      width: 450,
                      breakpoint: 'sm',
                      collapsed: { mobile: !isChatOpen, desktop: !isChatOpen },
                    }}
                    padding={0}
                  >
                    <AppShell.Main>
                      <div
                        className={`App ${
                          isSidebarCollapsed ? 'sidebar-collapsed' : ''
                        }`}
                      >
                        <Header
                          menuItems={menuItems}
                          openMobileMenu={() =>
                            sidebarMenuRef.current?.openMobileMenu()
                          }
                          onOpenScoutChat={() => {
                            setChatInitialText(undefined);
                            setShowFullScreenContinuationNotice(false);
                            setIsChatOpen(true);
                          }}
                        />
                        <SidebarMenu
                          ref={sidebarMenuRef}
                          menuItems={menuItems}
                          collapsed={isSidebarCollapsed}
                          onOpenMobileMenu={() => setIsMobileMenuOpen(true)}
                        />
                        <MainAppContent
                          shouldCollapseSidebar={(collapse) =>
                            setIsSidebarCollapsed(collapse)
                          }
                        />
                      </div>
                    </AppShell.Main>
                    <AppShell.Aside>
                      <ScoutChat
                        opened={isChatOpen}
                        onClose={() => {
                          setShowFullScreenContinuationNotice(false);
                          setIsChatOpen(false);
                        }}
                        initialText={chatInitialText}
                        showFullScreenContinuationNotice={
                          showFullScreenContinuationNotice
                        }
                      />
                    </AppShell.Aside>
                  </AppShell>
                </SpotlightSearch>
              </ScoutChatProvider>
            </MatterSearchProvider>
          </EmailAuthStatusProvider>
        </TermsProvider>
      );
  }
};

export default AuthenticatedAppLayout;

const MainAppContent = ({
  shouldCollapseSidebar,
}: {
  shouldCollapseSidebar: (collapse: boolean) => void;
}) => {
  const [shadowEdges, setShadowEdges] = useState<ShadowEdge[]>([]);
  const location = useLocation();

  useEffect(() => {
    shouldCollapseSidebar(
      ['/spreadsheet', '/claims'].includes(location.pathname),
    );
  }, [location.pathname, shouldCollapseSidebar]);

  return (
    <main
      className='main-content'
      style={{
        borderTop: shadowEdges.includes('top')
          ? '1px solid rgba(0, 0, 0, 0.1)'
          : 'none',
        borderLeft: shadowEdges.includes('left')
          ? '1px solid rgba(0, 0, 0, 0.1)'
          : 'none',
      }}
      onScroll={(e) => {
        const newShadowEdges: ShadowEdge[] = [];
        if ((e.target as HTMLElement).scrollTop > 0) {
          newShadowEdges.push('top');
        }
        if ((e.target as HTMLElement).scrollLeft > 0) {
          newShadowEdges.push('left');
        }
        setShadowEdges(newShadowEdges);
      }}
    >
      <AuthenticatedAppRoutes />
    </main>
  );
};
